// client/src/components/forms/FormField.tsx — label + control + error, ARIA-wired (04 §5.4) ·
// AUTH-004.
//
// The one departure from §5.4's listing is HOW the error is translated. The document writes
// `t(error)` with the schema's key in a variable; the i18n gate (04 §9.6.3, `check-i18n-keys`)
// rejects every computed key but `enums.*`, and it is right to — a key in a variable is a key
// the extractor cannot see and a translator cannot be handed. So the keys are resolved through
// `useValidationMessage()`, a literal table typed against `ValidationKey`: the schemas stay
// language-free as §5.4 requires, the gate stays whole, and a key missing from the table is a
// compile error. Anything that is NOT a validation key — a server message placed with
// `setError` — passes through as the text it already is. See DEVIATIONS D-183.

import { useCallback, useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';

import { formatCount } from '@/lib/format';
import { isValidationKey, type ValidationKey } from '@/lib/validation';

/** A text field's current length and the most it may hold, for `FormField`'s `limit`. */
export interface FieldLimit {
  length: number;
  max: number;
}

export interface FieldAria {
  id: string;
  'aria-invalid': boolean;
  'aria-describedby'?: string;
}

/** Resolves a `validation.*` key to its Romanian text; leaves any other string as it is. */
export function useValidationMessage(): (message: string | undefined) => string | undefined {
  const { t } = useTranslation();

  return useCallback(
    (message) => {
      if (message === undefined || !isValidationKey(message)) return message;
      return resolve(message, t);
    },
    [t],
  );
}

function resolve(message: ValidationKey, t: (key: string) => string): string {
  // Every member of ValidationKey, by literal. `switch` over the union: a key added to
  // VALIDATION_KEYS without a case here fails `noFallthroughCasesInSwitch`'s exhaustiveness
  // check below.
  switch (message) {
    case 'validation.required':
      return t('validation.required');
    case 'validation.email':
      return t('validation.email');
    case 'validation.passwordMin':
      return t('validation.passwordMin');
    case 'validation.passwordMatch':
      return t('validation.passwordMatch');
    case 'validation.code':
      return t('validation.code');
    case 'validation.tooLong':
      return t('validation.tooLong');
    case 'validation.positiveNumber':
      return t('validation.positiveNumber');
    case 'validation.buildYear':
      return t('validation.buildYear');
    case 'validation.percent':
      return t('validation.percent');
    case 'validation.date':
      return t('validation.date');
    case 'validation.currency':
      return t('validation.currency');
    case 'validation.cui':
      return t('validation.cui');
    case 'validation.cnp':
      return t('validation.cnp');
    case 'validation.dayOfMonth':
      return t('validation.dayOfMonth');
    case 'validation.month':
      return t('validation.month');
    case 'validation.endAfterStart':
      return t('validation.endAfterStart');
    case 'validation.onePrimaryTenant':
      return t('validation.onePrimaryTenant');
    case 'validation.fxRuleOnRon':
      return t('validation.fxRuleOnRon');
    case 'validation.ratePercent':
      return t('validation.ratePercent');
    case 'validation.withinContractPeriod':
      return t('validation.withinContractPeriod');
    case 'validation.paidDateOnlyWhenPaid':
      return t('validation.paidDateOnlyWhenPaid');
    case 'validation.dueAfterIssue':
      return t('validation.dueAfterIssue');
    case 'validation.vatWithinTotal':
      return t('validation.vatWithinTotal');
    case 'validation.categoryCode':
      return t('validation.categoryCode');
    case 'validation.endNotBeforeStart':
      return t('validation.endNotBeforeStart');
    case 'validation.expirationNotBeforeIssue':
      return t('validation.expirationNotBeforeIssue');

    case 'validation.tenancyEndAfterStart':
      return t('validation.tenancyEndAfterStart');
    case 'validation.depositBothOrNeither':
      return t('validation.depositBothOrNeither');
    case 'validation.alertOffsets':
      return t('validation.alertOffsets');
    case 'validation.quietHours':
      return t('validation.quietHours');
    case 'validation.startNotBeforeSigned':
      return t('validation.startNotBeforeSigned');
    case 'validation.userName':
      return t('validation.userName');
    case 'validation.cnpInvalid':
      return t('validation.cnpInvalid');
    default: {
      const exhaustive: never = message;
      return exhaustive;
    }
  }
}

export function FormField({
  label,
  error,
  required,
  hint,
  limit,
  className,
  children,
}: {
  label: string;
  /** A `validation.*` key from the schema, or already-Romanian text from the server. */
  error?: string | undefined;
  required?: boolean;
  hint?: string;
  /**
   * A bounded text field's live length and its maximum (D-3230). The field then shows
   * „1.234 / 4.000" under the control, in the error's red past the maximum, and the schema's
   * `validation.tooLong` names both numbers instead of „the maximum allowed". The caller passes
   * the length the schema measures (trimmed), so the counter and the refusal never disagree.
   */
  limit?: FieldLimit | undefined;
  /** Width utilities for the field as a whole; the default fills its column. */
  className?: string | undefined;
  children: (aria: FieldAria) => ReactNode;
}) {
  const { t } = useTranslation();
  const resolved = useValidationMessage()(error);
  const id = useId();
  const errorId = `${id}-err`;
  const hintId = `${id}-hint`;
  const countId = `${id}-count`;

  const over = limit !== undefined && limit.length > limit.max;
  // The schema's key carries no numbers (D-183: an error is a bare key), so the field that knows
  // the length words the refusal itself. Any other error — a server sentence included — stands.
  // `count` only picks the plural („4.215 caractere", „4.220 de caractere"); the figures shown
  // are the grouped ones.
  const message =
    over && error === 'validation.tooLong'
      ? t('validation.tooLongCount', {
          count: limit.length,
          length: formatCount(limit.length),
          max: formatCount(limit.max),
        })
      : resolved;

  const describedBy =
    [message ? errorId : hint ? hintId : undefined, limit ? countId : undefined]
      .filter((part) => part !== undefined)
      .join(' ') || undefined;

  // The frame is §5.4's; the third visual round (D-1843) set the label's size and weight, put a
  // glyph ahead of the error so it is not only red, and let the error enter with the 200 ms
  // rise the rest of the console uses. `data-invalid` on the wrapper is what the stylesheet reads
  // to colour the control's border and ring — a child selector cannot reach a sibling.
  return (
    <div
      className={`form-control ${className ?? 'w-full'}`}
      data-invalid={message ? 'true' : undefined}
    >
      <label className="label" htmlFor={id}>
        <span className="label-text">
          {label}
          {required ? (
            <span className="ml-0.5 text-error" aria-hidden="true">
              *
            </span>
          ) : null}
        </span>
      </label>
      {children(
        describedBy
          ? { id, 'aria-invalid': !!message, 'aria-describedby': describedBy }
          : { id, 'aria-invalid': !!message },
      )}
      {limit === undefined ? (
        <FieldNote hint={hint} hintId={hintId} message={message} errorId={errorId} />
      ) : (
        // The counter sits at the right of the hint or error's line, the way a counter is read
        // under a text box. Its figures are hidden from a screen reader, which hears the sentence
        // beside them instead: „1.234 / 4.000" spoken is a fraction.
        <div className="flex items-start gap-3">
          <FieldNote hint={hint} hintId={hintId} message={message} errorId={errorId} />
          <span
            id={countId}
            data-over={over ? 'true' : undefined}
            className={`mt-1.5 ml-auto shrink-0 text-xs tabular-nums ${over ? 'font-semibold text-red-700' : 'text-gray-500'}`}
          >
            <span aria-hidden="true">
              {t('common.characterCount', {
                length: formatCount(limit.length),
                max: formatCount(limit.max),
              })}
            </span>
            <span className="sr-only">
              {t('common.characterCountSpoken', {
                count: limit.max,
                length: formatCount(limit.length),
                max: formatCount(limit.max),
              })}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

function FieldNote({
  hint,
  hintId,
  message,
  errorId,
}: {
  hint: string | undefined;
  hintId: string;
  message: string | undefined;
  errorId: string;
}) {
  if (message) {
    return (
      // text-red-700, not text-error: 04 §10.2 R3 measures #ef4444 on white at 3.8:1 and
      // prescribes #b91c1c (6.5:1) for information-bearing red below large-text size. This
      // is the 12px form error the register names.
      <span
        id={errorId}
        role="alert"
        className="page-enter mt-1.5 inline-flex items-start gap-1 text-xs text-red-700"
      >
        <Icon name="x-circle" className="mt-px h-3.5 w-3.5 shrink-0" />
        <span>{message}</span>
      </span>
    );
  }
  return hint ? (
    <span id={hintId} className="mt-1.5 text-xs text-pretty text-gray-500">
      {hint}
    </span>
  ) : null;
}
